import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Button from "@/components/ui/button";
import { formatDateLong } from "@/utils";
import {
  supportTicketApprovalsApi,
  type SupportTicketApprovalItem,
} from "@/features/support-tickets/services/support-ticket-approvals.api";
import styles from "./SupportTicketApprovalsPanel.module.scss";

interface SupportTicketApprovalsPanelProps {
  items: SupportTicketApprovalItem[];
  isLoading: boolean;
  /** Karar sonrası liste yenilensin. */
  onDecided: () => void;
}

/**
 * Daire Başkanı / İşletme Müdürüne düşen "Kursiyer değişikliği / katılmama"
 * destek talepleri. Her kart talebin içeriğini gösterir; onay/red tek tıkla,
 * red gerekçesi opsiyonel not olarak yazılır.
 */
const SupportTicketApprovalsPanel: React.FC<SupportTicketApprovalsPanelProps> = ({
  items,
  isLoading,
  onDecided,
}) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState<Record<string, string>>({});
  const [busyTicketId, setBusyTicketId] = useState<string | null>(null);

  const decide = async (item: SupportTicketApprovalItem, approved: boolean) => {
    setBusyTicketId(item.ticketId);
    try {
      await supportTicketApprovalsApi.decide(item.ticketId, approved, comments[item.ticketId]);
      toast.success(
        t(approved ? "supportTicketCategory.approvals.approved" : "supportTicketCategory.approvals.rejected"),
      );
      onDecided();
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(message || t("supportTicketCategory.approvals.error"));
    } finally {
      setBusyTicketId(null);
    }
  };

  if (isLoading) {
    return <div className={styles.empty}>{t("common.loading")}</div>;
  }
  if (items.length === 0) {
    return <div className={styles.empty}>{t("supportTicketCategory.approvals.empty")}</div>;
  }

  return (
    <div className={styles.list}>
      {items.map((item) => {
        const busy = busyTicketId === item.ticketId;
        return (
          <div key={item.ticketId} className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.requestNo}>#{item.requestNo}</span>
              <span className={styles.date}>{formatDateLong(item.createdAt)}</span>
            </div>
            <h4 className={styles.title}>{item.title}</h4>
            {item.description && <p className={styles.description}>{item.description}</p>}
            <dl className={styles.meta}>
              <div>
                <dt>{t("supportTicketCategory.approvals.requester")}</dt>
                <dd>
                  {item.requester.firstName} {item.requester.lastName}
                  {item.requester.departmentName ? ` · ${item.requester.departmentName}` : ""}
                </dd>
              </div>
              {item.education && (
                <div>
                  <dt>{t("supportTicketCategory.approvals.education")}</dt>
                  <dd>{item.education.name}</dd>
                </div>
              )}
            </dl>
            <textarea
              className={styles.comment}
              rows={2}
              maxLength={1000}
              value={comments[item.ticketId] ?? ""}
              onChange={(e) => setComments((prev) => ({ ...prev, [item.ticketId]: e.target.value }))}
              placeholder={t("supportTicketCategory.approvals.commentPlaceholder")}
              disabled={busy}
            />
            <div className={styles.actions}>
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={busy}
                onClick={() => decide(item, false)}
              >
                {t("supportTicketCategory.approvals.reject")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                loading={busy}
                disabled={busy}
                onClick={() => decide(item, true)}
              >
                {t("supportTicketCategory.approvals.approve")}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SupportTicketApprovalsPanel;
